import React from 'react';
import PropTypes from 'prop-types';
import { useCustomer } from '../../../../contexts/CustomerContext';
import { heroForOrderType, PRODUCT_GRID_ID, fillTemplate, gradeYear } from './giftHeroConfig';
import Item, { canRenderPreview } from './Item';
import { ORDER_TYPES } from './orderType';

/**
 * The banner above the products. Draws whatever giftHeroConfig hands it — there
 * is no per-order-type markup in this file, and adding an order type must not
 * add one.
 *
 * CATALOG ONLY: rendered by GiftShop, so a product screen opens on the product
 * rather than under a banner for the shelf the customer just left.
 */

// Honour a reduced-motion preference: a long smooth scroll is exactly the kind
// of movement that setting exists to stop.
const scrollToProducts = () => {
  const target = document.getElementById(PRODUCT_GRID_ID);
  if (!target) return;

  const reduced = window.matchMedia
    && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  target.scrollIntoView({ behavior: reduced ? 'auto' : 'smooth', block: 'start' });
};

export default function GiftHero({
  orderType, addons, poses, backgrounds,
}) {
  const hero = heroForOrderType(orderType);

  // Copy can name the student: "{{first_name}}'s Keepsakes", "their 5th grade
  // year". Read from the session rather than passed down through three
  // components -- this is who the customer IS, not data the page fetched.
  //
  // ⚠️ A PLACEHOLDER MUST NEVER REACH THE SCREEN. `fillTemplate` returns null
  // unless EVERY value in the template resolved, so a missing name or an
  // unphraseable grade falls back to copy written without it rather than
  // rendering "{{first_name}}'s Keepsakes" at a customer. `gradeYear` is the
  // guard on the second: the column really does contain "ST" (a staff record)
  // and "0".
  const { customer } = useCustomer();
  const student = customer?.currentStudent;
  const values = {
    first_name: String(student?.firstName || '').trim(),
    grade: gradeYear(student?.grade),
  };

  const headline = fillTemplate(hero.headline, values) || hero.headlineFallback || hero.headline;
  const body = fillTemplate(hero.body, values) || hero.bodyFallback || hero.body;

  // The customer's own pose, composited into a product -- the same render the
  // shelf tiles use, via the same component, so the two can never disagree
  // about which child is in the picture.
  //
  // Returns null when the product is not on THIS customer's shelf (audience
  // scoping means a senior and a second-grader see different lists) or when the
  // poses have not arrived yet; the caller then draws the labelled placeholder.
  const previewFor = (cssName) => {
    if (!cssName || !poses?.length) return null;

    const addon = addons?.find((a) => a.cssName === cssName);
    if (!addon || !canRenderPreview(addon, cssName)) return null;

    return (
      <Item
        previewOnly
        addon={addon}
        variant={cssName}
        background={backgrounds?.[0]}
        pose={poses[0]}
      />
    );
  };

  return (
    <section
      aria-labelledby="gift-hero-headline"
      className="mt-8 overflow-hidden rounded-gift-hero"
      style={{ backgroundColor: hero.background }}
    >
      {/* 280px tall ONLY where the images are shown. Below `xl` the banner is
          text alone and sizes to its own content, rather than holding 280px of
          empty colour open. */}
      <div className="flex flex-col xl:h-[280px] xl:flex-row">
        {/* ⚠️ LESS VERTICAL PADDING ON A PHONE (Alex, 2026-09-29). The banner is
            the first thing on the page and at `p-5` it spent 40px of a 844px
            screen on empty tan above and below the copy, pushing the products
            further below the fold. The SIDE padding is unchanged -- that is the
            page gutter, and the text would run to the edge without it. */}
        <div className="flex shrink-0 flex-col justify-center px-5 py-3.5 md:p-8 xl:w-[560px] xl:p-14">
          <h2
            id="gift-hero-headline"
            className="m-0 text-[30px] font-extrabold text-gift-ink md:text-[36px] xl:text-[44px]"
            style={{ lineHeight: 1.05, letterSpacing: '-0.02em' }}
          >
            {headline}
          </h2>

          <p
            className="mb-0 mt-3 text-[15px] md:text-[16px] xl:text-[18px]"
            style={{ lineHeight: 1.45, color: '#3A4250' }}
          >
            {body}
          </p>

          {/* ⚠️ NOT ON A PHONE (Alex, 2026-09-29), for the same reason the
              decorative pair is not: below `md` this banner's job is to say
              where the customer is in one or two lines and get out of the way.
              The button only scrolls to the products directly beneath it --
              on a phone they are already the next thing on screen, so it cost
              a third of the banner's height to save no scrolling at all.
              `max-md:hidden`, never a bare `hidden`, which Bootstrap declares
              `!important` so nothing could un-hide it. */}
          <div className="max-md:hidden">
            {/* Dark pill, not the accent: the accent belongs to "Continue to
                checkout" in the bar below, and two accent pills on one screen
                would compete for the same press. */}
            <button
              type="button"
              onClick={scrollToProducts}
              className={'mt-6 inline-flex h-[44px] items-center justify-center rounded-gift-pill '
                + 'border-0 bg-gift-ink px-7 text-[15px] font-bold text-white transition '
                + 'hover:opacity-90 xl:h-[48px]'}
            >
              {hero.buttonLabel}
            </button>
          </div>
        </div>

        {/* ⚠️ FIXED BOXES, NOT A SHARE OF THE ROW — this is the whole point.
            These were `w-1/2` of a flexing column, so every viewport width gave
            the images a different box and `object-fit: cover` re-cropped them to
            fit it: at 1383px the children were cut off mid-face, and by 764px
            the announcement was a sliver. A fixed 288x280 box crops the artwork
            ONCE, the same way at every width, and the banner's slack shows as
            background instead of as a new crop.

            HIDDEN BELOW `xl`, deliberately, and for EVERY ORDER TYPE — this is
            the one component every pricelist renders, so the banner behaves the
            same whether the config hands it senior artwork or a school
            placeholder. 560px of text plus 576px of images needs ~1136px of
            content before anything has to give; under that the images would
            have to shrink again, which is the bug. On a phone they also cost a
            screen of vertical space for decoration, so the banner drops to text
            alone rather than stacking them underneath.

            ⚠️ A stacked-on-mobile variant (images on top at 180/240px, via
            `flex-col-reverse`) shipped in f23234383 and is reverted here. Do
            not reintroduce it without asking — it is the second time this has
            been lost.

            `aria-hidden`: decoration for copy that already says what this is. */}
        {/* ⚠️ `max-xl:hidden`, NEVER `hidden xl:flex`. Bootstrap defines
            `.hidden { display: none !important }` app-wide, so the bare class
            can never be un-hidden by a responsive pair — written that way the
            images stayed invisible at EVERY width, 1600px included. This names
            a distinct class that only ever hides, so nothing has to win it
            back. Same reason ItemBuilder's buy button uses `max-sm:hidden`. */}
        <div className="ml-auto flex shrink-0 max-xl:hidden" aria-hidden="true">
          {hero.images.map((image, i) => (
            <div
              // eslint-disable-next-line react/no-array-index-key -- a fixed pair, never reordered
              key={`${orderType}-hero-${i}`}
              /* ONE FIXED BOX, not a share of the row. The artwork is cropped
                 once, identically at every width the images are shown at. */
              className="h-[280px] w-[288px]"
            >
              {/* NOTHING when the product cannot be composited -- the slot is
                  simply empty and the banner's own colour shows through.

                  ⚠️ This used to draw a dashed box captioned "SENIOR HERO
                  IMAGE 1", deliberately ugly so it could not ship unnoticed.
                  It nearly did: the fallback is not a dev-only state. It is
                  reached in production whenever the named product is not on
                  this customer's shelf (audience scoping) or the poses have
                  not arrived yet, which is EVERY first paint. A customer would
                  have read developer text on a live storefront.

                  An empty slot is safe because a banner with no artwork is
                  already a shipped, designed state -- it is exactly what every
                  order type shows below `xl`. */}
              {previewFor(image.product)}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

GiftHero.defaultProps = {
  orderType: ORDER_TYPES.UNKNOWN,
  addons: [],
  poses: [],
  backgrounds: [],
};

GiftHero.propTypes = {
  // From resolveOrderType. An unrecognised value falls back to the UNKNOWN
  // entry rather than rendering nothing.
  orderType: PropTypes.oneOf(Object.values(ORDER_TYPES)),
  // The same three the shelf tiles composite from, passed down rather than
  // fetched here: one fetch, one answer, and the banner cannot show a different
  // student or a different background from the products underneath it.
  // eslint-disable-next-line react/forbid-prop-types
  addons: PropTypes.array,
  // eslint-disable-next-line react/forbid-prop-types
  poses: PropTypes.array,
  // eslint-disable-next-line react/forbid-prop-types
  backgrounds: PropTypes.array,
};

export { scrollToProducts };
